import { useCallback, useState } from "react";
import { Language, Message } from "@lango/api/src/types";
import { trpc } from "@lango/api";
import { useBot } from "@/api/useBot";
import { useAudio } from "@/api/useAudio";
import { getRandomElement, getUUID } from "@/utils/utils";
import { useSound } from "@/api/useSound";
import { useUser } from "@/api/useUser";
import { useTranscribe } from "@/api/useTranscribe";
import { EVENTS, trackEvent } from "@/services/analytics";

import { usePaywall } from "./usePaywall";

const GREETINGS: { [key in Language]: string[] } = {
  English: ["Hi"],
  Spanish: ["Hola"],
  French: ["Salut"],
  Italian: ["Ciao"],
  Portuguese: ["Oi"],
  German: ["Hallo"],
  Japanese: ["こんにちは"],
  Korean: ["안녕하세요"],
  Russian: ["Привет"],
  Chinese: ["你好"],
  Arabic: ["مرحبا"],
  Dutch: ["Hallo"],
  Greek: ["Γειά σας"],
  Ukrainian: ["Привіт"],
  Polish: ["Cześć"],
  Norwegian: ["Hei"],
  Danish: ["Hej"],
  Finnish: ["Hei"],
  Swedish: ["Hej"],
  Indonesian: ["Halo"],
};

export const useChat = (id: string) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const { incCount, isActive, isLoading: isPaywallLoading } = usePaywall(id);
  const { playText } = useAudio();
  const { data: bot, isLoading } = useBot(id);
  const { play: playStart } = useSound("/sounds/startChat.mp3", "audio/mpeg");
  const { data: user } = useUser();
  const { transcribe, isLoading: isTranscribeLoading } = useTranscribe();
  const {
    error,
    mutateAsync: sendMessage,
    isLoading: isSendMessageLoading,
  } = trpc.sendMessage.useMutation({
    onMutate: (input) => {
      trackEvent(EVENTS.MESSAGE_SENT);
      setMessages((prev) => {
        const prevMessages = input.newConversation ? [] : prev || [];
        const lastMessage = prevMessages[prevMessages.length - 1];
        if (
          lastMessage?.messageType === "hint" ||
          lastMessage?.messageType === "error"
        ) {
          prevMessages.pop();
        }
        return [
          ...prevMessages,
          {
            id: getUUID(),
            sender: "user",
            text: input.text,
            messageType: "text",
            timestamp: new Date().toISOString(),
          },
        ];
      });
    },
    onSuccess: async (data, { newConversation }) => {
      const lastMessage = data[data.length - 1];
      if (lastMessage.messageType === "text" && lastMessage.sender !== "user") {
        playText(lastMessage.text, bot!.voice);
      }
      checkContinuation(data);
      setMessages(() => {
        return data;
      });
      if (!newConversation) {
        incCount();
      }
    },
    onError: () => {
      trackEvent(EVENTS.MESSAGE_FAILED);
      setMessages((prev) => {
        if (!prev) {
          return [];
        }
        const lastMessage = prev[prev.length - 1];
        return [
          ...prev.slice(0, -1),
          { ...lastMessage, messageType: "error" },
        ] as Message[];
      });
    },
  });

  const {
    mutateAsync: continueConversation,
    isLoading: isContinueMessageLoading,
  } = trpc.continueConversation.useMutation({
    onSuccess: (data) => {
      setMessages(() => {
        return data;
      });
    },
  });

  const {
    mutateAsync: mutateChatSuggestion,
    isLoading: isChatSuggestionLoading,
  } = trpc.chatSuggestion.useMutation({
    onMutate: () => {
      trackEvent(EVENTS.SUGGESTION_CLICKED);
      setMessages((prev) => {
        const prevMessages = prev || [];
        const lastMessage = prevMessages[prevMessages.length - 1];
        if (
          lastMessage?.messageType === "hint" ||
          lastMessage?.messageType === "error"
        ) {
          prevMessages.pop();
        }
        return prevMessages;
      });
    },
    onSuccess: (data) => {
      setMessages((prev) => {
        return [
          ...(prev || []),
          {
            id: getUUID(),
            sender: "user",
            messageType: "hint",
            hints: data,
            text: "",
            timestamp: new Date().toISOString(),
          },
        ];
      });
    },
  });

  const checkContinuation = useCallback(
    (messages: Message[]) => {
      const lastMessage = messages?.[messages?.length - 1];
      if (!lastMessage) {
        return;
      }
      if (lastMessage.messageType !== "text" || !lastMessage.text) {
        return;
      }
      let lastMessageText = lastMessage.text;
      let lastMessageSender = lastMessage.sender;
      let isSecondMessage = Math.random() <= 0.3;
      let isQuestion =
        lastMessageText.indexOf("?") > -1 ||
        lastMessageText.indexOf("？") > -1 ||
        lastMessageText.indexOf("¿") > -1;
      if (
        isSecondMessage &&
        !isQuestion &&
        lastMessageSender !== "user" &&
        messages.length > 4
      ) {
        continueConversation({ messages, id });
      }
    },
    [continueConversation, id]
  );

  const retry = useCallback(() => {
    const lastMessage = messages?.[messages?.length - 1];
    if (!lastMessage || lastMessage.messageType !== "error") {
      return;
    }
    setMessages((prev) => {
      return [...(prev || []).slice(0, -1)];
    });
    sendMessage({ text: lastMessage.text, id, messages });
  }, [sendMessage, messages, id]);

  const onChatSuggestion = useCallback(() => {
    mutateChatSuggestion({
      messages,
      language: bot!.language,
      targetLanguage: user!.language,
    });
  }, [bot, messages, mutateChatSuggestion]);

  const onStartConversation = useCallback(async () => {
    playStart();
    sendMessage({
      messages,
      id,
      text: getRandomElement(GREETINGS[bot!.language]),
      newConversation: true,
    });
  }, [sendMessage, id, messages, bot, playStart]);

  const onVoiceMessage = useCallback(
    async (audioData: string) => {
      const data = await transcribe(audioData);
      sendMessage({ id, text: data, messages });
    },
    [messages, id]
  );

  return {
    isPaywallActive: isActive,
    messages: messages || [],
    sendMessageError: Boolean(error),
    retry,
    onChatSuggestion,
    sendMessage,
    onStartConversation,
    onVoiceMessage,
    isLoading: isLoading || isPaywallLoading,
    isBotThinking: isSendMessageLoading || isContinueMessageLoading,
    isUserThinking: isChatSuggestionLoading || isTranscribeLoading,
  };
};
